import { HStack, Skeleton } from '@chakra-ui/react';
import NextLink from 'next/link';
import { FC } from 'react';

import { LectureIcon } from '@/components/ui/features/originals/RelatedLecturesSection/components/LectureIcon';
import { LectureCard } from '@/components/ui/shared/lecture/LectureCard/LectureCard';
import { LectureCardTag } from '@/components/ui/shared/lecture/LectureCard/LectureCardTags/LectureCardTags';
import { LectureCardImage } from '@/components/ui/shared/lecture/LectureCardImage/LectureCardImage';
import { useAnalytics } from '@/context/Analytics';
import { SlugIdParam, useQueryParams } from '@/hooks/useQueryParams';
import { useResource, useResources } from '@/lib/datx-jsonapi-react';
import { getOriginalSeries } from '@/queries/originals-series';
import { queryOriginalsLectures } from '@/queries/thin-lectures';
import { LECTURE_CARD_IMAGE_FALLBACK } from '@/utils/constants';
import { dateToString } from '@/utils/date';

export const RelatedLecturesMobile: FC = () => {
  const analytics = useAnalytics();

  const { id: seriesId } = useQueryParams<{ id: string }>({
    id: SlugIdParam,
  });

  const { data: series } = useResource(getOriginalSeries(seriesId));
  const { data: items, error, mutate } = useResources(queryOriginalsLectures(series?.subject?.id));

  if (items.length === 0 && !error) {
    return (
      <HStack display={{ base: 'flex', xl: 'none' }} mt={4} pb={3} spacing={5} overflowX="hidden" alignItems="stretch">
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton h="280px" minW="250px" border="1px solid #CACCCC" key={i} />
        ))}
      </HStack>
    );
  }

  return (
    <HStack display={{ base: 'flex', xl: 'none' }} mt={4} pb={3} spacing={5} overflowX="scroll" alignItems="stretch">
      {items.map((lecture) => (
        <NextLink key={lecture.id} href={`/lecture/${lecture.linkSlug}`} passHref legacyBehavior>
          <LectureCard
            mutate={mutate}
            lecture={lecture}
            key={lecture.id}
            onClick={() =>
              analytics.track('originals_related_lecture_click', {
                lecture_name: lecture.title,
                lecture_id: lecture.id,
                originals_id: seriesId,
              })
            }
            minW="250px"
            as="a"
            border="1px solid #CACCCC"
            image={
              <LectureCardImage src={lecture.posterThumbnailUrl || LECTURE_CARD_IMAGE_FALLBACK} alt={lecture.title} />
            }
            tag={<LectureCardTag tagItem={lecture.tag} />}
            icon={<LectureIcon lecturePackage={lecture.package} />}
            authors={lecture.sortedProfiles}
            name={lecture.title}
            heldAt={dateToString(lecture.heldAt, 'dd MMMM yyyy')}
            eventName={lecture?.event?.name}
            awards={lecture?.awards}
          />
        </NextLink>
      ))}
    </HStack>
  );
};
